import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowDown, PencilRuler, Gem, Hammer, Boxes, Eye,
  HardHat, Handshake, MapPin, Check,
} from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import CTASection from "@/components/CTASection";
import { SITE, IMAGES, SERVICES, WHY_POINTS, PROJECTS, PROCESS_STEPS, SERVICE_AREAS } from "@/lib/site";

const WHY_ICONS = [PencilRuler, Gem, Hammer, Boxes, Eye, HardHat, Handshake, MapPin];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative h-screen min-h-[640px] overflow-hidden grain">
      <motion.img
        src={IMAGES.hero}
        alt="Custom kitchen with dark cabinetry and marble countertops by Hive Interiors, GTA"
        style={{ y }}
        className="absolute inset-0 w-full h-[120%] object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-background/40" />

      <motion.div style={{ opacity }} className="relative h-full max-w-7xl mx-auto px-6 md:px-12 pt-28 flex flex-col justify-end pb-24 md:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <Overline className="mb-6">Custom Cabinetry & Interior Renovations — GTA</Overline>
        </motion.div>
        <h1 className="font-heading text-[13vw] sm:text-6xl md:text-7xl tracking-tight leading-[1.04]">
          <MaskedLine delay={0.3}>Custom Cabinets.</MaskedLine>
          <MaskedLine delay={0.45}>Complete Renovations.</MaskedLine>
          <MaskedLine delay={0.6} className="italic text-primary">
            Beautiful Interiors.
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-8 max-w-xl text-base md:text-lg text-foreground/80 leading-relaxed"
        >
          Transform your kitchen, bathroom, and home with custom interior solutions designed around your space, lifestyle, and vision.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-consultation-button"
            className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-300 hover:bg-accent hover:text-accent-foreground group"
          >
            Get a Free Consultation
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/projects"
            data-testid="hero-projects-button"
            className="inline-flex items-center gap-3 border border-foreground/40 px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold text-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
          >
            View Our Projects
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-8 right-8 hidden md:flex items-center gap-3 text-muted-foreground"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.div>
    </section>
  );
}

function Manifesto() {
  return (
    <section data-testid="manifesto-section" className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-3">
          <Overline>01 / Who We Are</Overline>
        </Reveal>
        <div className="md:col-span-9">
          <Reveal delay={0.1}>
            <p className="font-heading text-3xl md:text-5xl tracking-tight leading-[1.2]">
              {SITE.name} specializes in custom kitchen cabinets, kitchen & bathroom renovations, and complete home interior solutions across the GTA. We combine{" "}
              <span className="italic text-primary">modern design</span>,{" "}
              <span className="italic text-primary">smart functionality</span>, and{" "}
              <span className="italic text-primary">quality craftsmanship</span> to create beautiful, customized spaces built to last.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/about"
              data-testid="manifesto-about-link"
              className="mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] font-bold text-primary group"
            >
              More About Us
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section data-testid="services-section" className="py-24 md:py-36 bg-surface border-y border-border">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24">
          <Reveal className="md:col-span-3">
            <Overline>02 / What We Do</Overline>
          </Reveal>
          <div className="md:col-span-9 flex flex-wrap items-end justify-between gap-6">
            <Reveal delay={0.1}>
              <h2 className="font-heading text-4xl md:text-6xl tracking-tight leading-[1.08]">
                Services Built Around <span className="italic text-primary">Your Home</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/services"
                data-testid="services-view-all-link"
                className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] font-bold text-primary group"
              >
                All Services
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {SERVICES.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.08} className={i % 3 === 1 ? "lg:translate-y-10" : ""}>
              <Link
                to={s.href}
                data-testid={`service-card-${s.num}`}
                className="group block bg-card border border-border"
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={s.img}
                    alt={s.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 font-heading italic text-5xl text-white/90 drop-shadow-lg">
                    {s.num}
                  </span>
                </div>
                <div className="p-6 md:p-8">
                  <h3 className="font-heading text-2xl tracking-tight group-hover:text-primary transition-colors duration-300">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhySection() {
  return (
    <section data-testid="why-section" className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24">
          <Reveal className="md:col-span-3">
            <Overline>03 / Why Hive Interiors</Overline>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-9">
            <h2 className="font-heading text-4xl md:text-6xl tracking-tight leading-[1.08] max-w-3xl">
              The Details Are the <span className="italic text-primary">Difference</span>
            </h2>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          {WHY_POINTS.map((w, i) => {
            const Icon = WHY_ICONS[i];
            return (
              <div
                key={w.title}
                data-testid={`why-point-${i + 1}`}
                className="bg-background p-8 md:p-10 group hover:bg-surface transition-colors duration-500"
              >
                <Icon className="w-6 h-6 text-primary mb-6 transition-transform duration-500 group-hover:-translate-y-1" strokeWidth={1.5} />
                <h3 className="font-heading text-xl tracking-tight">{w.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProjectsSection() {
  const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-12"];
  return (
    <section data-testid="featured-projects-section" className="py-24 md:py-36 bg-surface border-y border-border">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24">
          <Reveal className="md:col-span-3">
            <Overline>04 / Selected Work</Overline>
          </Reveal>
          <div className="md:col-span-9 flex flex-wrap items-end justify-between gap-6">
            <Reveal delay={0.1}>
              <h2 className="font-heading text-4xl md:text-6xl tracking-tight leading-[1.08]">
                Featured <span className="italic text-primary">Projects</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/projects"
                data-testid="projects-view-all-link"
                className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] font-bold text-primary group"
              >
                View Gallery
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.title} delay={(i % 4) * 0.08} className={spans[i]}>
              <div data-testid={`project-tile-${i + 1}`} className="group relative overflow-hidden aspect-[16/10]">
                <img
                  src={p.img}
                  alt={p.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 p-6">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-primary">{p.cat}</p>
                  <h3 className="font-heading text-2xl md:text-3xl tracking-tight text-white mt-1">{p.title}</h3>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessSection() {
  return (
    <section data-testid="process-section" className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24">
          <Reveal className="md:col-span-3">
            <Overline>05 / The Process</Overline>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-9">
            <h2 className="font-heading text-4xl md:text-6xl tracking-tight leading-[1.08]">
              Five Steps to a Space <span className="italic text-primary">You'll Love</span>
            </h2>
          </Reveal>
        </div>
        <div>
          {PROCESS_STEPS.map((step, i) => (
            <Reveal key={step.num} delay={i * 0.05}>
              <div
                data-testid={`process-step-${step.num}`}
                className="group grid grid-cols-12 gap-6 items-center py-10 md:py-14 border-t border-border last:border-b"
              >
                <span className="col-span-3 md:col-span-2 font-heading italic text-6xl md:text-8xl text-muted/60 group-hover:text-primary transition-colors duration-500 leading-none">
                  {step.num}
                </span>
                <h3 className="col-span-9 md:col-span-4 font-heading text-2xl md:text-4xl tracking-tight">
                  {step.title}
                </h3>
                <p className="col-span-12 md:col-span-6 text-sm md:text-base text-muted-foreground leading-relaxed md:pl-8">
                  {step.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceAreaSection() {
  return (
    <section data-testid="service-area-section" className="py-24 md:py-36 bg-cream text-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-3">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#B89024]">06 / Service Area</p>
        </Reveal>
        <div className="md:col-span-9">
          <Reveal delay={0.1}>
            <h2 className="font-heading text-4xl md:text-6xl tracking-tight leading-[1.08]">
              Proudly Serving the <span className="italic text-[#B89024]">Entire GTA</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#0A0A0A]/70 leading-relaxed max-w-2xl">
              Hive Interiors works with homeowners throughout the Greater Toronto Area, bringing premium custom cabinetry and renovation craftsmanship to your neighbourhood.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-4" data-testid="service-area-list">
              {SERVICE_AREAS.map((area) => (
                <li key={area} className="flex items-center gap-2 text-sm md:text-base font-semibold tracking-wide">
                  <Check className="w-4 h-4 text-[#B89024]" strokeWidth={2.5} />
                  {area}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <SEO
        title="Custom Kitchen Cabinets & Renovations GTA | Hive Interiors"
        description="Hive Interiors designs and builds custom kitchen cabinets, kitchen & bathroom renovations, cabinet refacing and complete home interiors across the GTA — Scarborough, Toronto, Markham, Vaughan, Mississauga and beyond."
      />
      <Hero />
      <EditorialMarquee />
      <Manifesto />
      <ServicesSection />
      <WhySection />
      <ProjectsSection />
      <ProcessSection />
      <ServiceAreaSection />
      <CTASection />
    </main>
  );
}
